import DeleteOutlined from "@ant-design/icons/DeleteOutlined";
import EditOutlined from "@ant-design/icons/EditOutlined";
import EllipsisOutlined from "@ant-design/icons/EllipsisOutlined";
import InfoCircleOutlined from "@ant-design/icons/InfoCircleOutlined";
import PercentageOutlined from "@ant-design/icons/PercentageOutlined";
import SettingOutlined from "@ant-design/icons/SettingOutlined";
import ShareAltOutlined from "@ant-design/icons/ShareAltOutlined";
import VerticalAlignTopOutlined from "@ant-design/icons/VerticalAlignTopOutlined";
import Spin from "antd/es/spin";
import { MOBILE_SEARCH } from "common/constants";
import { getTradeExtraDetailsRoute } from "utils/routes";
import useHistoryWithQuery from "utils/hooks/useHistoryWithQuery";
import { LanguageNameSpaces, TradeActionType, TradeData } from "@shared/types";
import React, { useContext, useEffect, useState } from "react";
import { TradeActionsContext } from "@shared/features/trades/common/contexts";
import useMedia from "utils/hooks/useMedia";
import styled, { useTheme } from "styled-components";
import { IconWrapper } from "../common/styles";
import { useShareResultData } from "@shared/features/trades/common/hooks";
import { ItemType } from "antd/es/menu/interface";
import Dropdown from "common/Dropdown/Dropdown";
import Tooltip from "common/Tooltips/Tooltip";
import {
  CardsSelectorContext,
  ContextProps,
} from "@shared/common/CardsList/CardsSelector.context.ts";
import ThunderboltOutlined from "@ant-design/icons/ThunderboltOutlined";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import {
  useDisplayTradeDetails,
  useSubUserPermissions,
} from "@shared/utils/hooks/misc.ts";
import { TRADE_EXTRA_DETAILS_TABS } from "@shared/common/constants.ts";
import { EllipsisVertical } from "lucide-react";

interface TradeActionsDropdownProps {
  tradeData: TradeData;
  isOpenTrades?: boolean;
  isCardsView?: boolean;
  showBulkActions?: boolean;
}

const TradeActionsDropdown = ({
  tradeData,
  isOpenTrades,
  isCardsView,
  showBulkActions,
}: TradeActionsDropdownProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.TRADES);
  const { isNewLook } = useTheme();
  const isMobile = useMedia([`(max-width: ${MOBILE_SEARCH})`], [true], false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const { historyPush } = useHistoryWithQuery();
  const { isReadOnly } = useSubUserPermissions();
  const displayTradeDetails = useDisplayTradeDetails();

  const {
    getMoveEntryErrorMessage,
    getSellPartiallyErrorMessage,
    getEditTradeErrorMessage,
    handleTradeActionClick,
    getIsTradeReadOnlyAccount,
  } = useContext(TradeActionsContext)!;
  const { setIsEditMode } =
    useContext<ContextProps<TradeData> | undefined>(CardsSelectorContext) || {};
  const isTradeReadOnlyAccount = getIsTradeReadOnlyAccount(tradeData);

  const {
    isInvalid: shareResultInvalid,
    showAnimation,
    onShareClick,
    shareTradeImageLoading,
  } = useShareResultData(tradeData);

  useEffect(() => {
    if (!shareTradeImageLoading && dropdownOpen) closeDropdown();
  }, [shareTradeImageLoading]);

  const onEditOrdersClick = () => {
    historyPush(
      getTradeExtraDetailsRoute(
        location.pathname,
        tradeData.id,
        TRADE_EXTRA_DETAILS_TABS.ORDERS,
        true
      )
    );
  };

  const onEditConfigClick = () => {
    historyPush(
      getTradeExtraDetailsRoute(
        location.pathname,
        tradeData.id,
        TRADE_EXTRA_DETAILS_TABS.CONFIGURATION,
        true
      )
    );
  };

  const openTradeExtraDetails = (tradeData: TradeData) => {
    historyPush(
      getTradeExtraDetailsRoute(
        location.pathname,
        tradeData.id,
        TRADE_EXTRA_DETAILS_TABS.GENERAL
      )
    );
  };

  const closeDropdown = () => setDropdownOpen(false);

  let menuItems: ItemType[] = [];
  if (isMobile && displayTradeDetails) {
    menuItems.push({
      key: "tradeDetails",
      icon: <InfoCircleOutlined />,
      label: t("actions.trade_details"),
      onClick: () => {
        closeDropdown();
        openTradeExtraDetails(tradeData);
      },
    });
  }
  const openTradesMenuItems: ItemType[] = [
    {
      key: "cancelTrade",
      icon: <DeleteOutlined />,
      label: t("actions.close_trade"),
      onClick: () => {
        closeDropdown();
        handleTradeActionClick(TradeActionType.CLOSE_TRADE, tradeData);
      },
      className: isTradeReadOnlyAccount ? "invalid" : undefined,
    },
    {
      key: "editOrders",
      icon: <EditOutlined />,
      label: t("actions.edit_orders"),
      onClick: () => {
        closeDropdown();
        handleTradeActionClick(
          TradeActionType.EDIT_ORDERS,
          tradeData,
          onEditOrdersClick
        );
      },
      className:
        isTradeReadOnlyAccount || !!getEditTradeErrorMessage(tradeData)
          ? "invalid"
          : undefined,
    },
    {
      key: "editConfig",
      icon: <SettingOutlined />,
      label: t("actions.edit_configuration"),
      onClick: () => {
        closeDropdown();
        handleTradeActionClick(
          TradeActionType.EDIT_CONFIGURATION,
          tradeData,
          onEditConfigClick
        );
      },
      className:
        isTradeReadOnlyAccount || !!getEditTradeErrorMessage(tradeData)
          ? "invalid"
          : undefined,
    },
    {
      key: "moveNearestEntry",
      icon: <VerticalAlignTopOutlined />,
      label: t("actions.move_nearest_entry"),
      onClick: () => {
        closeDropdown();
        handleTradeActionClick(TradeActionType.MOVE_NEAREST_ENTRY, tradeData);
      },
      className:
        isTradeReadOnlyAccount || !!getMoveEntryErrorMessage(tradeData)
          ? "invalid"
          : undefined,
    },
    {
      key: "sellPartially",
      icon: <PercentageOutlined />,
      label: t("actions.sell_partially"),
      onClick: () => {
        closeDropdown();
        handleTradeActionClick(TradeActionType.SELL_PARTIALLY, tradeData);
      },
      className:
        isTradeReadOnlyAccount || !!getSellPartiallyErrorMessage(tradeData)
          ? "invalid"
          : undefined,
    },
  ];
  if (isOpenTrades && !isReadOnly) {
    menuItems = [...menuItems, ...openTradesMenuItems];
  }
  if (isCardsView) {
    menuItems.push({
      key: "shareResults",
      label: (
        <ShareResultWrapper $showBg={showAnimation}>
          <StyledShareAltOutlined />
          {t("drawer.share_results")}
          <Spin
            spinning={shareTradeImageLoading}
            style={{ marginLeft: 10 }}
            size="small"
          />
        </ShareResultWrapper>
      ),
      onClick: () => onShareClick(),
      className:
        isTradeReadOnlyAccount || shareResultInvalid ? "invalid" : undefined,
    });
    if (isOpenTrades && showBulkActions && !isReadOnly) {
      menuItems.push({
        key: "bulkActions",
        icon: <ThunderboltOutlined />,
        label: t("actions.bulk_actions"),
        onClick: () => setIsEditMode?.(tradeData),
        className: isTradeReadOnlyAccount ? "invalid" : undefined,
      });
    }
  }

  return (
    <div onClick={(e) => e.stopPropagation()}>
      <Dropdown
        title={t("actions.trade_actions")}
        menu={{ items: menuItems }}
        trigger={["click"]}
        onOpenChange={setDropdownOpen}
        open={dropdownOpen}
      >
        <Tooltip title={t("actions.more_options")}>
          {isNewLook ? (
            <NewLookTrigger
              type="button"
              aria-label={t("actions.more_options")}
            >
              <NewLookEllipsis size={18} strokeWidth={2} />
            </NewLookTrigger>
          ) : (
            <IconWrapper>
              <StyledEllipsisOutlined />
            </IconWrapper>
          )}
        </Tooltip>
      </Dropdown>
    </div>
  );
};

export default React.memo(TradeActionsDropdown);

const StyledEllipsisOutlined = styled(EllipsisOutlined)`
  transform: rotate(90deg);
  font-size: 30px;
  cursor: pointer;
  color: ${(p) => p.theme.colors.primaryGrey};
`;
const NewLookTrigger = styled(IconWrapper).attrs({ as: "button" })`
  padding: 0;
  border: 0;
  background: transparent;
  font: inherit;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  &:focus-visible {
    outline: 2px solid ${(p) => p.theme.colors.primaryBlue};
    outline-offset: 1px;
  }
`;
const NewLookEllipsis = styled(EllipsisVertical)`
  cursor: pointer;
  color: ${(p) => p.theme.colors.secondaryText};
`;
const ShareResultWrapper = styled.div<{ $showBg?: boolean }>`
  ${(p) =>
    p.$showBg &&
    `
    background-color: ${p.theme.colors.lightBlue};
    color: ${p.theme.colors.primaryBlue};
    width: fit-content;
    border-radius: 4px;
    padding: 2px 6px;
    left: -6px;
    position: relative;
  `};
`;
const StyledShareAltOutlined = styled(ShareAltOutlined)`
  font-size: 12px;
  margin-right: 8px;
`;
